import pako from 'pako';
import { encodeMid } from '@mi/mid-tools';
import { generateUniqueId, formateSendData, getUrlQuery } from './utils/utils';
import { CONSTANT, CACHE_TIME, reportApi } from './utils/constant';
import localStorage from './utils/localStorage';

export default class base {
  constructor() {
    this._projectConfig = {};
    this._collectionInfo = {};
    this._baseInfo = {};
    this._event = [];
    this._needSend = false;
    this._allowRecord = false;
    this._mode = null; // 模式 record: 记录模式，reduction: 场景还原模式
    this._isInit = false;
    this._reductionInstance = null;
    this._cacheDataTimeId = null;
  }

  async _setScene(scene, url = '') {
    try {
       // 如果项目没有初始化，则直接返回
      if (!this._isInit) { return; }
      // 如果url中包含_online_inspection_debug，_reduction_host, 且不是线上环境，则走场景还原模式
      const querys = getUrlQuery(url);
      if (querys._online_inspection_debug && querys._reduction_host && this._projectConfig.env && this._projectConfig.env !== 'production') {
        this._mode = 'reduction';
        const debugId = querys._online_inspection_debug;
        const reductionHost = decodeURIComponent(querys._reduction_host);
        await this._initReduction(debugId, reductionHost);
      } else {
        const id = generateUniqueId();
        const traceId = await this.getTraceId();
        await this._beforeInitScene();
        this.setBaseInfo({
          ...this._collectionInfo,
          scene,
          traceId: traceId || id,
          id,
          url,
          time: `${Date.now()}`,
          projectId: this._projectConfig.projectId || '',
          version: this._projectConfig.version || '',
          env: this._projectConfig.env || '',
        });
        this._mode = 'record';
        this._allowRecord = true;
        this._checkSamplingRate();
        this._initCatchError();
        // 开始设置场景时，才自动化记录
        this._initBehaviorRecord();
      }
    } catch (e) {
      console.log(e, '>>>>_setSceneError');
    }
  }

  // 初始化场景之前的事项
  async _beforeInitScene() {
    try {
      // 查看是否有没有发送的数据
      const data = await localStorage.getItem(CONSTANT.NOT_SEND);
      const parseData = JSON.parse(data);
      // 如果当前数据存在，且允许上报，则上报
      if (parseData?._needSend) {
        // todo：需要再走预检流程
        const info = formateSendData(parseData.baseInfo, parseData.event);
        this._send(info);
      }
    } catch (e) {
      console.log('_beforeInitScene: ', e);
    }
  }

  // 获取traceId
  async getTraceId() {
    try {
    const traceIdData = JSON.parse(await localStorage.getItem(CONSTANT.TRACE_ID));
    // 如果缓存有traceId，则用缓存，用于串联各个场景
    if (traceIdData.time && traceIdData.traceId && Date.now() - traceIdData.time < 3000) {
      // 记录继承的tradeId，如果存在，这条数据需要上报，保证场景链路的完整性
      this._needSend = true;
      return traceIdData.traceId;
    }
    } catch (e) {}
    return '';
  }

  // 初始化场景化还原
  async _initReduction() {}

  // 获取时间
  getTime() {
    return `${Date.now()}`;
  }

  // 基础事件上报
  recordEvent(eventData) {
    // 如果当前条件不允许记录，则不上报
    if ((this._mode !== 'record' && !this._allowRecord) || !eventData.name) {
      return;
    }
    const info = {
      ...eventData,
      id: generateUniqueId(),
      time: this.getTime(),
    };
    this._event.push(info);
    console.log(info, '>>>>>>>>>>>>>>>>>>>>>>>>>>eventData');
    // 缓存数据
    this.cacheWaitingSendData();
  }

  // 记录api事件
  /**
   *
   * @param {
   *   name: '',
   *   info: {
   *    url: '',
   *    method: '',
   *    response: {},
   *    request: {},
   *   }
   * } param0
   */
  recordApiEvent({ name = '接口请求', info = {}, tags = {} }, { needSend = false } = {}) {
    if (needSend) {
      this._needSend = needSend;
    }
    // 处理网络请求
    this.recordEvent({
      type: 'data',
      tags,
      name,
      info,
    });
  }

  cacheWaitingSendData() {
    if (this._cacheDataTimeId) {
      clearTimeout(this._cacheDataTimeId);
    }
    this._cacheDataTimeId = setTimeout(() => {
      localStorage.setItem(CONSTANT.NOT_SEND, JSON.stringify({
        baseInfo: this._baseInfo,
        event: this._event,
        _needSend: this._needSend,
      }));
    }, CACHE_TIME);
  }

  // 包装api
  apiWrapper = (fn) => {
    const instance = this;
    return async function (...rest) {
      // 场景还原模式
      if (instance._mode === 'reduction' && instance._reductionInstance) {
        const res = await instance._reductionInstance.taskGetData(...rest);
        return res;
      }
      const startTime = Date.now();
      const [url, options] = [...rest];
      const response = await fn(...rest);
      const endTime = Date.now();
      try {
        const cloneRes = await response.clone();
        // 添加延时，优先执行业务流程
        setTimeout(async () => {
          let res = {};
          try {
            res = await cloneRes.json() || {};
          } catch (e) {
            console.log('cloneRes.json()', e);
          }
          // todo：对接口返回状态码进行处理
          instance.recordApiEvent({
            tags: {
              apiUrl: url,
              requestStatus: cloneRes.status === 200 || res.code === 0 ? 'success' : 'fail',
              code: res.code,
            },
            info: {
              name: url,
              path: url,
              params: options.params || options.body,
              response: {
                status: cloneRes.status,
                statusText: cloneRes.statusText,
                headers: cloneRes.headers,
                body: res,
              },
              duration: endTime - startTime,
            },
          }, { needSend: cloneRes.status !== 200 || ![999999999301, 999999999302, 0].includes(res.code) });
        });
      } catch (error) {
        console.log(error, '>>>>apiWrapper');
      }
      return response;
    };
  };

  // 记录错误事件
  recordErrorEvent({ name = '', info = {}, tags = {} }, { needSend = true, immediatelySend = false } = {}) {
    this._needSend = needSend;
    this.recordEvent({
      type: 'error',
      name,
      tags,
      info,
    });
    if (immediatelySend) {
      this._needSend = true;
      this.sendData();
    }
  }

  // 记录资源加载失败事件
  recordResourceFailedEvent({ name = '', info = {}, tags = {} }) {
    this.recordEvent({
      type: 'resource_failed',
      name,
      tags,
      info,
    });
  }

  /**
   * 记录信息事件
   * @param {
   *    name: '',
   *    info: {},
   * } infoData
   */
  recordInfoEvent({ name = '', info = {}, tags = {} }) {
    this.recordEvent({
      type: 'info',
      name,
      tags,
      info,
    });
  }

  /**
   * 记录点击行为事件
   * @param {
   *    name: '',
   *    info: {},
   * } behaviorData
   */
  recordTouchEvent({ name = '', info = {}, tags = {} }, fromAutoRecordBehavior) {
    // 如果是自动记录行为，则不记录
    if (!info.tag || (this._projectConfig._autoRecordBehavior && !fromAutoRecordBehavior)) {
      return;
    }
    this.recordEvent({
      type: 'touch',
      isFrame: true,
      name: `【点击】${name}`,
      tags,
      info,
    });
  }

  /**
   * 记录曝光行为事件
   * @param {
  *    name: '',
  *    info: { spm, iid},
  * } behaviorData
  */
 recordVisibleEvent({ name = '', info = {}, tags = {} }) {
   this.recordEvent({
     type: 'visible',
     name: `【曝光】${name}`,
     tags,
     info,
   });
 }

  /**
   * 记录点击行为事件
   * @param {
   *    name: '',
   *    info: {},
   * } behaviorData
   */
  recordScrollEvent({ name = '', info = {}, tags, ...rest }, fromAutoRecordBehavior) {
    // 如果是自动记录行为，且不来自于自动记录，则不记录
    if ((this._projectConfig._autoRecordBehavior && !fromAutoRecordBehavior) || !info.tag || !(info.offsetY !== undefined || info.offsetX !== undefined)) {
      return;
    }
    this.recordEvent({
      name: `【滚动】${name}`,
      tags,
      info,
      ...rest,
      type: 'scroll',
      isFrame: true,
    });
  }

  recordScroll = (() => {
    const scrollTimeouts = new Map();
    let startTime = null;
    let isScrolling = false;
    return ({ offsetY = 0, offsetX = 0 }, tag) => {
      // 如果是自动记录行为或者非记录模式则不记录
      if (this._projectConfig._autoRecordBehavior || this._mode !== 'record') {
        return;
      }
      // console.log('offsetY:', offsetY, 'offsetX:', offsetX);
      if (!isScrolling) {
        startTime = Date.now();
        isScrolling = true;
      }
      const timeoutId = scrollTimeouts.get(tag);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      const newTimeoutId = setTimeout(() => {
        this.recordScrollEvent({
          name: '滚动行为',
          time: startTime, // 开始滚动的时间戳
          info: {
            tag,
            offsetY,
            offsetX,
            duration: Date.now() - startTime,
          },
        }, true);
        // console.log('记录滚动11111:', Date.now() - startTime, offsetY);
        isScrolling = false;
        startTime = null;
        scrollTimeouts.delete(tag);
      }, 150);
      scrollTimeouts.set(tag, newTimeoutId);
    };
  })();

  // 补充基础信息
  setBaseInfo(info) {
    Object.assign(this._baseInfo, info);
  }

  setEnv(env) {
    this._projectConfig.env = env;
    this.setBaseInfo({ env });
  }

  // 设置用户id
  // 需要加密处理
  setUid(uid) {
    try {
      if (!uid) { return; }
      const id = encodeMid(uid);
      this._collectionInfo.xiaomi_id = id;
      this.setBaseInfo({ xiaomi_id: id });
    } catch (e) {
      console.log(e, '>>>>setUidError');
    }
  }

  setVersion(version) {
    this._projectConfig.version = version;
    this.setBaseInfo({ version });
  }

  async sendData() {
    if (!this._isInit) { return; }
    try {
      // 发送期间不允许记录
      this._beforeSendDataHook();
      // 预检
      const allow = await this._preCheck();
      if (allow) {
        // 当前数据需要上传，则将traceId缓存起来，携带了相同traceId的场景下次一定上报
        await localStorage.setItem(CONSTANT.TRACE_ID, JSON.stringify({
          traceId: this._baseInfo.traceId,
          time: Date.now(),
        }));
        const data = formateSendData(this._baseInfo, this._event);
        // 重置
        await this._reset();
        await this._send(data);
      } else {
        await this._reset();
      }
    } catch (e) {
      console.log(e, '>>>>sendDataError');
    }
  }

  // 校验采样率
  _checkSamplingRate() {
    if (!this._projectConfig._samplingRate) { return false; }
    // this._projectConfig._samplingRate 要大于等于1小于等于100
    if (this._projectConfig._samplingRate <= 0 || this._projectConfig._samplingRate > 100) { return false; }
    // 1-100的设置，1表示1%的概率，100表示100%的概率
    if (Math.floor(Math.random() * 100 * 100) + 1 <= this._projectConfig._samplingRate * 100) {
      this._needSend = true;
    };
  }

  // 预检
  async _preCheck() {
    // 非记录模式不上传
    // 如果不是从其他已经上报的场景跳转过来，且不强制上传，且不满足采样率，则不上传
    // 如果是从别的场景跳转过来，则需要上传
    if (this._mode !== 'record' || (!this._preTraceId && !this._needSend)) { return; }
    return true;
  }

  // 发送数据前的处理
  async _beforeSendDataHook() {
    // 发送期间不允许记录
    this._allowRecord = false;
    if (this._cacheDataTimeId) {
      clearTimeout(this._cacheDataTimeId);
    }
    // 删除缓存的数据
    await localStorage.setItem(CONSTANT.NOT_SEND, '');
  }

  // 上报
  async _send(data) {
    try {
      // 压缩数据
      // RN环境不需要自己压缩
      const body = window?.location?.href ? pako.gzip(JSON.stringify(data)) : JSON.stringify(data);
      // 上报
      await fetch(reportApi, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
          'Content-Encoding': 'gzip',
        },
        body,
      });
      console.log('send success');
    } catch (e) {
      console.log('send error', e);
    }
  }

  // 重置, 不论是否上报成功都重置
  _reset() {
    this._event = [];
    this._mode = null;
    this._allowRecord = false;
    this._baseInfo = {};
    this._needSend = false;
    this._cacheDataTimeId = null;
    this._reductionInstance = null;
    this._afterSendDataHook();
  }
}
